import type { useAuth } from "@clerk/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@ndd/ui";
import type { ReactElement, ReactNode } from "react";
import type { AppointmentNoteResponse } from "../../../../shared/api-types";
import { RichTextEditor } from "@/components/v2/rich-text-editor";
import { useFormDirtyGuard } from "@/components/v2/use-form-dirty-guard";
import { EditorConflictBanner, EditorSaveStatus } from "./editor-status";
import { useAppointmentNoteAutosave } from "./use-appointment-note-autosave";

type ClerkGetToken = ReturnType<typeof useAuth>["getToken"];

/**
 * The Appointment note: the RD's rough working note, autosaved as they type.
 * The editor card fills the tab and pins its footer (Enhance status and
 * action, supplied by the workspace) under the text.
 */
export function AppointmentNoteEditor({
  appointmentId,
  initial,
  getToken,
  footer,
}: {
  appointmentId: string;
  initial: AppointmentNoteResponse;
  getToken: ClerkGetToken;
  footer?: ReactNode;
}): ReactElement {
  const autosave = useAppointmentNoteAutosave({ appointmentId, initial, getToken });
  const dirtyGuard = useFormDirtyGuard(autosave.snapshot.dirty);

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col">
        <RichTextEditor
          initialValue={initial.document}
          replacement={autosave.replacement}
          shouldFillAvailableHeight
          onChange={autosave.edit}
          toolbarEnd={<EditorSaveStatus status={autosave.snapshot.status} onRetry={autosave.retry} />}
          alert={autosave.snapshot.status === "conflict" ? (
            <EditorConflictBanner
              subject="note"
              onUseLatest={autosave.useLatest}
              onSaveMine={autosave.saveMine}
            />
          ) : undefined}
          footer={footer}
        />
      </div>

      <AlertDialog
        open={dirtyGuard.showDiscardDialog}
        onOpenChange={dirtyGuard.handleDiscardDialogOpenChange}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave before this note is saved?</AlertDialogTitle>
            <AlertDialogDescription>
              The latest changes to your appointment note could be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={() => dirtyGuard.confirmDiscard(() => {})}>
              Leave without saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
